import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import NotificationsIcon from 'assets/notifications.svg?react';
import { OnboardingLayout } from 'components/OnboardingLayout';
import { useOnboardingProgress } from 'hooks/useOnboardingProgress';
import { useMapState } from 'state/useMapState';
import {
  getCurrentPositionFromCapacitor,
  requestGeolocationPermissionsForNative,
} from 'hooks/useUserPosition';
import { PushNotifications } from '@capacitor/push-notifications';
import { ZOOM_LEVELS } from 'features/mapping/mapping.constants';
import { useCriticalAlertsOnboarding } from 'hooks/useCriticalAlertsOnboarding';

const useStyles = makeStyles()(() => ({
  image: {
    width: '90vmin',
    height: 'auto',
    maxWidth: 345,
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

/**
 * Tracks which native permission prompt is currently visible so we can swap
 * the layout content for an instructional footer while the OS dialog is up.
 *
 * The notifications phase is set on every platform; on Android 12 and below
 * the OS doesn't show a prompt, so `requestPermissions()` resolves
 * immediately and the footer flashes briefly. Android 13+ and iOS show the
 * prompt as expected.
 */
type PromptPhase = 'idle' | 'location' | 'notifications';

export const PermissionsContent = (): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { updateIncidentMapState } = useMapState();
  const { shouldShowCriticalAlertsStep } = useCriticalAlertsOnboarding();
  const [promptPhase, setPromptPhase] = useState<PromptPhase>('idle');
  const progress = useOnboardingProgress('permissions');

  const setInitialMapLocationToUsersLocation = async (): Promise<void> => {
    const [error, result] = await getCurrentPositionFromCapacitor();
    if (error || !result) return;
    const { coords } = result;
    updateIncidentMapState({
      center: { lat: coords.latitude, lng: coords.longitude },
      zoom: ZOOM_LEVELS.REGION,
    });
  };

  const handleContinue = async (): Promise<void> => {
    setPromptPhase('location');
    const permissionsGranted = await requestGeolocationPermissionsForNative();
    if (permissionsGranted) {
      setInitialMapLocationToUsersLocation().then(() => {
        // Intentionally not awaiting so this runs in the background without the user waiting
      });
    }

    setPromptPhase('notifications');
    const notificationPermissionsStatus =
      await PushNotifications.requestPermissions();

    setPromptPhase('idle');

    const redirectToCriticalAlerts = shouldShowCriticalAlertsStep(
      notificationPermissionsStatus.receive === 'granted',
    );

    if (redirectToCriticalAlerts) {
      history.push('/critical_alerts_permission');
    } else {
      history.push('/onboarding_notifications');
    }
  };

  const isPrompting = promptPhase !== 'idle';

  const footerText = isPrompting ? (
    <Typography textAlign="center" sx={{ px: 2 }}>
      {promptPhase === 'location' ? (
        <Trans i18nKey="permissions.locationPrompt">
          Tap <b>Allow While Using the App</b> so we know which incidents are
          near you.
        </Trans>
      ) : (
        <Trans i18nKey="permissions.notificationsPrompt">
          Tap <b>Allow</b> to receive notifications about incidents near you.
        </Trans>
      )}
    </Typography>
  ) : undefined;

  return (
    <OnboardingLayout
      buttonTitle={isPrompting ? undefined : t('permissions.button')}
      onClick={handleContinue}
      footerText={footerText}
      progress={progress}
      hideProgress={isPrompting}
    >
      {!isPrompting && (
        <Stack spacing={2}>
          <Box className={classes.iconContainer}>
            <NotificationsIcon className={classes.image} />
          </Box>

          <Typography
            variant="h2"
            align="center"
            data-testid="onboarding-title"
          >
            {t('permissions.title')}
          </Typography>

          <Typography>{t('permissions.description')}</Typography>
        </Stack>
      )}
    </OnboardingLayout>
  );
};
